import * as Dialog from '@cannect/new-components/atoms/Dialog'
import { cn } from '@cannect/lib/utils'
import { ChevronLeft, CircleHelp, Loader2 } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'
import { TRAIL_STEP_TOTAL } from './paths'
import { SUPPORT_WHATSAPP_URL } from './copy'

interface TrailShellProps {
  open: boolean
  onClose: () => void
  stepIndex: number
  onBack?: () => void
  children: ReactNode
  footer?: ReactNode
  hideProgress?: boolean
  showHelp?: boolean
  surface?: boolean
  hideHeader?: boolean
  // Muda a cada passo/tela; a área rolável volta ao topo quando ela muda.
  scrollKey?: string
  // Porcentagem fixa da barra; substitui o cálculo por passo.
  progressOverride?: number
  headerTitle?: { title: string; subtitle: string }
}

const iconButtonClass =
  'inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-solid border-[#E4E4E4] bg-white text-[#1A1A1A]'

export const TrailCta = ({
  children,
  disabled,
  loading,
  onClick,
  type = 'button'
}: {
  children: ReactNode
  disabled?: boolean
  // Chamada à API em andamento: bloqueia novos cliques e mostra o spinner no lugar do texto.
  loading?: boolean
  onClick?: () => void
  type?: 'button' | 'submit'
}) => (
  <button
    type={type}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    onClick={onClick}
    className={cn(
      'inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border-0 bg-marketplace-800 px-6 text-sm font-semibold text-white disabled:opacity-50',
      loading && 'cursor-wait'
    )}>
    {loading ? <Loader2 className="size-5 animate-spin" aria-hidden /> : children}
  </button>
)

export const TrailGhost = ({
  children,
  disabled,
  onClick,
  href
}: {
  children: ReactNode
  disabled?: boolean
  onClick?: () => void
  href?: string
}) => {
  const className =
    'mt-3 inline-flex w-full items-center justify-center bg-transparent text-sm font-medium text-[#1A1A1A]'

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        {children}
      </a>
    )
  }

  return (
    <button type="button" disabled={disabled} onClick={onClick} className={cn(className, 'border-0')}>
      {children}
    </button>
  )
}

const ProgressBar = ({ progress }: { progress: number }) => (
  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E9EDDD]">
    <div className="h-full rounded-full bg-marketplace-800" style={{ width: `${progress}%` }} />
  </div>
)

const HelpButton = () => (
  <a
    href={SUPPORT_WHATSAPP_URL}
    target="_blank"
    rel="noreferrer"
    aria-label="Falar com o atendimento"
    className={iconButtonClass}>
    <CircleHelp className="size-[18px]" />
  </a>
)

export const TrailShell = ({
  open,
  onClose,
  stepIndex,
  onBack,
  children,
  footer,
  hideProgress,
  showHelp,
  surface,
  hideHeader,
  scrollKey,
  progressOverride,
  headerTitle
}: TrailShellProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [scrollKey])

  const progress =
    progressOverride ?? Math.max(8, Math.round((Math.min(stepIndex, TRAIL_STEP_TOTAL) / TRAIL_STEP_TOTAL) * 100))
  const backButton = onBack ? (
    <button type="button" onClick={onBack} aria-label="Voltar" className={iconButtonClass}>
      <ChevronLeft className="size-5" />
    </button>
  ) : null

  return (
    <Dialog.Root open={open} onOpenChange={(value) => !value && onClose()}>
      <Dialog.Content
        hideClose
        overlayClassName="z-[1000001] bg-white md:bg-[rgba(67,63,62,0.4)] md:backdrop-blur-[8px]"
        className="left-0 top-0 z-[1000001] flex h-dvh max-h-none w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-white p-0 font-['Inter',sans-serif] shadow-none md:left-1/2 md:top-1/2 md:h-auto md:max-h-[min(92dvh,880px)] md:min-h-[min(92dvh,720px)] md:w-[min(calc(100%-48px),520px)] md:max-w-[520px] md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-[24px] md:shadow-[0_24px_80px_rgba(42,47,29,0.18)] [&_*]:font-['Inter',sans-serif]">
        <Dialog.Title className="sr-only">Triagem do programa de perda de peso</Dialog.Title>
        {hideHeader ? null : (
          <header className="shrink-0 bg-white px-4 pb-3 pt-4 md:px-6">
            {headerTitle ? (
              <>
                <div className="relative flex items-center justify-center">
                  <div className="absolute left-0">{backButton}</div>
                  <div className="text-center">
                    <p className="m-0 text-base font-semibold text-[#1A1A1A]">{headerTitle.title}</p>
                    <p className="m-0 text-xs text-[#6B6560]">{headerTitle.subtitle}</p>
                  </div>
                </div>
                <div className="mt-3 flex">
                  <ProgressBar progress={progress} />
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                {backButton}
                {!hideProgress ? <ProgressBar progress={progress} /> : <div className="flex-1" />}
                {showHelp ? <HelpButton /> : null}
              </div>
            )}
          </header>
        )}

        <div
          ref={scrollRef}
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-6 pt-3 md:px-6',
            surface ? 'bg-[#F5F6F8]' : 'bg-white'
          )}>
          <main className="mx-auto flex min-h-0 w-full max-w-[480px] flex-1 flex-col">{children}</main>
        </div>

        {footer ? (
          <div
            className={cn(
              'shrink-0 bg-white px-4 pb-5 pt-3 md:px-6',
              surface && 'border-t border-solid border-[#EEEEEE]'
            )}>
            <div className="mx-auto flex w-full max-w-[480px] flex-col">{footer}</div>
          </div>
        ) : null}
      </Dialog.Content>
    </Dialog.Root>
  )
}
